import { useState } from 'react';
import { IoBookOutline, IoCheckboxOutline, IoSquareOutline, IoCheckmarkCircleOutline } from 'react-icons/io5';
import './BookSelector.css';

const BookSelector = ({ books, selectedBooks, onBooksChange }) => {
  const [activeTestament, setActiveTestament] = useState('Old');

  const handleBookToggle = (bookName) => {
    const isSelected = selectedBooks.includes(bookName);
    
    if (isSelected) {
      onBooksChange(selectedBooks.filter(book => book !== bookName));
    } else {
      onBooksChange([...selectedBooks, bookName]);
    }
  };

  const handleTestamentToggle = (testament) => {
    const testamentBooks = books[testament] || [];
    const selectedInTestament = testamentBooks.filter(book => selectedBooks.includes(book));
    
    if (selectedInTestament.length === testamentBooks.length) {
      // All selected, deselect all
      onBooksChange(selectedBooks.filter(book => !testamentBooks.includes(book)));
    } else {
      // Some or none selected, select all
      const newSelection = [...new Set([...selectedBooks, ...testamentBooks])];
      onBooksChange(newSelection);
    }
  };

  const clearAll = () => {
    onBooksChange([]);
  };

  const getTestamentStats = (testament) => {
    const testamentBooks = books[testament] || [];
    const selectedCount = testamentBooks.filter(book => selectedBooks.includes(book)).length;
    return { selected: selectedCount, total: testamentBooks.length };
  };

  const getTestamentIcon = (testament) => {
    const stats = getTestamentStats(testament);
    if (stats.selected === 0) {
      return IoSquareOutline;
    } else if (stats.selected === stats.total) {
      return IoCheckmarkCircleOutline;
    } else {
      return IoCheckboxOutline;
    }
  };

  const oldTestamentStats = getTestamentStats('Old');
  const newTestamentStats = getTestamentStats('New');

  return (
    <div className="book-selector">
      <div className="book-selector-header">
        <div className="header-info">
          <h3>Select books to log</h3>
          <p>Choose entire books of the Bible to log for your selected readers</p>
        </div>
        
        <div className="header-stats">
          <span className="total-selected">
            {selectedBooks.length} book{selectedBooks.length !== 1 ? 's' : ''} selected
          </span>
          {selectedBooks.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="clear-all-button"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      <div className="testament-tabs">
        <button
          type="button"
          className={`testament-tab ${activeTestament === 'Old' ? 'active' : ''}`}
          onClick={() => setActiveTestament('Old')}
        >
          <IoBookOutline className="tab-icon" />
          <span>Old Testament</span>
          <small>({oldTestamentStats.selected}/{oldTestamentStats.total} selected)</small>
        </button>
        
        <button
          type="button"
          className={`testament-tab ${activeTestament === 'New' ? 'active' : ''}`}
          onClick={() => setActiveTestament('New')}
        >
          <IoBookOutline className="tab-icon" />
          <span>New Testament</span>
          <small>({newTestamentStats.selected}/{newTestamentStats.total} selected)</small>
        </button>
      </div>

      <div className="testament-content">
        <div className="testament-header">
          <button
            type="button"
            className="select-testament-button"
            onClick={() => handleTestamentToggle(activeTestament)}
          >
            {(() => {
              const IconComponent = getTestamentIcon(activeTestament);
              return <IconComponent className="testament-toggle-icon" size={20} />;
            })()}
            <span>
              {getTestamentStats(activeTestament).selected === getTestamentStats(activeTestament).total
                ? `Deselect all ${activeTestament} Testament`
                : `Select all ${activeTestament} Testament`
              }
            </span>
          </button>
        </div>

        <div className="books-grid">
          {(books[activeTestament] || []).map(bookName => {
            const isSelected = selectedBooks.includes(bookName);
            return (
              <div
                key={bookName}
                className={`book-item ${isSelected ? 'selected' : ''}`}
                onClick={() => handleBookToggle(bookName)}
              >
                <div className="book-checkbox">
                  {isSelected ? (
                    <IoCheckmarkCircleOutline className="checkbox-icon selected" />
                  ) : (
                    <IoSquareOutline className="checkbox-icon" />
                  )}
                </div>
                <div className="book-info">
                  <span className="book-name">{bookName}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedBooks.length > 0 && (
        <div className="selection-summary">
          <h4>Selected books ({selectedBooks.length})</h4>
          <div className="selected-books-list">
            {selectedBooks.map(bookName => (
              <span key={bookName} className="book-tag">
                {bookName}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookSelector;
